import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup, GooglePreview } from '../../../features/Report';
import { Tooltip, Table, CopyButton, CustomData, ProgressBar } from '../../../components';

export const ContentSection = ({ id, siteUrl, data }) => {
  const [titleTagExpanded, setTitleTagExpanded] = useState(false);
  const [metaDescriptionExpanded, setMetaDescriptionExpanded] = useState(false);
  const [headingsExpanded, setHeadingsExpanded] = useState(false);
  const [contentAnalysisExpanded, setContentAnalysisExpanded] = useState(false);
  const [altAttributeExpanded, setAltAttributeExpanded] = useState(false);
  const [inPageLinksExpanded, setInPageLinksExpanded] = useState(false);

  // Extract transformed data with fallbacks for loading state
  const defaultCriteria = { status: 'error', iconType: 'error', isEmpty: true, message: '', importance: { level: 0, text: '' }, solvability: { level: 0, text: '' } };
  const titleTag = data?.titleTag || defaultCriteria;
  const metaDescription = data?.metaDescription || defaultCriteria;
  const headings = data?.headings || defaultCriteria;
  const contentAnalysis = data?.contentAnalysis || defaultCriteria;
  const altAttribute = data?.altAttribute || defaultCriteria;
  const inPageLinks = data?.inPageLinks || defaultCriteria;
  const phoneNumbers = data?.phoneNumbers || defaultCriteria;
  const webFeeds = data?.webFeeds || defaultCriteria;
  const googlePreview = data?.googlePreview || defaultCriteria;
  const errorCount = data?.errorCount || 0;
  const toImproveCount = data?.toImproveCount || 0;

  // Build headings count table row
  const headingsCountRow = headings?.data?.counts 
    ? [
        String(headings?.data?.counts?.h1 || 0),
        String(headings?.data?.counts?.h2 || 0),
        String(headings?.data?.counts?.h3 || 0),
        String(headings?.data?.counts?.h4 || 0),
        String(headings?.data?.counts?.h5 || 0)
      ]
    : ['0', '0', '0', '0', '0'];

  // Build headings summary table data
  const headingsSummaryData = headings?.data?.headings?.length > 0
    ? [
        ['Level', 'Value'],
        ...headings?.data?.headings?.map(h => [h?.tag || '', h?.heading || '']) || []
      ]
    : [['Level', 'Value']];

  // Build content analysis table data
  const contentAnalysisData = contentAnalysis?.data?.keywords?.length > 0
    ? [
        ['Words/Phrases', 'Frequency'],
        ...contentAnalysis?.data?.keywords?.map(k => [k?.keyword || '', String(k?.amount || 0)]) || []
      ]
    : [['Words/Phrases', 'Frequency']];

  // Build alt attribute missing images table data
  const missingAltTableData = altAttribute?.data?.missingAltImages?.length > 0
    ? [
        ['Image URL'],
        ...altAttribute?.data?.missingAltImages?.map(img => [img?.src || '']) || []
      ]
    : [['Image URL']];

  // Build in-page links table data
  const inPageLinksTableData = inPageLinks?.data?.links?.length > 0
    ? [
        ['Anchor', 'Type', 'Follow'],
        ...inPageLinks?.data?.links?.map(link => [
          link?.anchor || 'No Anchor Text',
          link?.type || '',
          link?.follow || ''
        ]) || []
      ]
    : [['Anchor', 'Type', 'Follow']];

  // Build phone numbers table data
  const phoneNumbersTableData = phoneNumbers?.data?.phoneNumbers?.length > 0
    ? [
        ['Phone'],
        ...(phoneNumbers?.data?.phoneNumbers?.map(p => [p?.value || '']) || [])
      ]
    : [['Phone']];

  // Build web feeds table data
  const webFeedsTableData = webFeeds?.data?.feeds?.length > 0
    ? [
        ['URL', 'Format'],
        ...(webFeeds?.data?.feeds?.map(f => [f?.href || '', f?.format || '']) || [])
      ]
    : [['URL', 'Format']];

  return (
    <ResultsItem 
      id={id}
      title="Content" 
      tooltip="Content is an essential part of your SEO strategy. From product pages to blog posts, you want to optimize your content for search engines and users to get the most value from it. That's why our SEO checker takes a 360-degree look at your website's content and its SEO."
      errorCount={errorCount}
      toImproveCount={toImproveCount}
    >
      <ResultSubItem
        title="Title Tag"
        tooltip="A title tag should be 20-70 characters (200-569 pixels) and appears in search results."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={titleTag.iconType}
                showTooltip={false} 
              />
            }
          >
            {!titleTag?.isEmpty && titleTag?.data ? (
              <>
                <p className="text-primary">{titleTag?.message}</p>
                <p className="text-secondary">{titleTag?.data?.value}</p>
                <p className="text-secondary">Length: {titleTag?.data?.length || 0} character(s) ({titleTag?.data?.pixelLength || 0} pixels)</p>
              </>
            ) : (
              <p className="text-primary">{titleTag?.message || `The title tag for ${siteUrl} is missing.`}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={titleTagExpanded}
            onClick={() => setTitleTagExpanded(!titleTagExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={titleTagExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Title Tag Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Keep title tags between 20-70 characters (200-569 pixels)</li>
                <li>Place your core keyword at the start of your title tag, if possible</li>
                <li>Make title tags transparent, but also enticing for your audience</li>
              </ul>
            </IconContent>
          }
          sideContent={
            titleTag?.importance?.level > 0 || titleTag?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {titleTag?.importance?.level > 0 && (
                  <PriorityIndicator text={titleTag?.importance?.text || ''} level={titleTag?.importance?.level || 0} />
                )}
                {titleTag?.solvability?.level > 0 && (
                  <PriorityIndicator text={titleTag?.solvability?.text || ''} level={titleTag?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/seo/title-tag/" target="_blank" rel="noopener noreferrer">
                    What Is a Title Tag?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/seo/learn/how-to-write-a-title-tag/" target="_blank" rel="noopener noreferrer">
                    How to Write a Title Tag: 8 Best Practices
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>
      
      <ResultSubItem
        title="Meta Description"
        tooltip="A meta description should be 70-160 characters (600-940 pixels) and appears in search results."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={metaDescription.iconType}
                showTooltip={false} 
              />
            }
          >
            {!metaDescription?.isEmpty && metaDescription?.data ? (
              <>
                <p className="text-primary">{metaDescription?.message}</p>
                <p className="text-secondary">{metaDescription?.data?.value}</p>
                <p className="text-secondary">Length: {metaDescription?.data?.length || 0} character(s) ({metaDescription?.data?.pixelLength || 0} pixels)</p>
              </>
            ) : (
              <p className="text-primary">{metaDescription?.message || `The meta description for ${siteUrl} is missing.`}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={metaDescriptionExpanded}
            onClick={() => setMetaDescriptionExpanded(!metaDescriptionExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={metaDescriptionExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Meta Description Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Keep meta descriptions between 70-160 characters (600-940 pixels)</li>
                <li>Include your core keyword and related keywords, if possible</li>
                <li>Use meta descriptions to summarize your page</li>
              </ul>
            </IconContent>
          }
          sideContent={
            metaDescription?.importance?.level > 0 || metaDescription?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {metaDescription?.importance?.level > 0 && (
                  <PriorityIndicator text={metaDescription?.importance?.text || ''} level={metaDescription?.importance?.level || 0} />
                )}
                {metaDescription?.solvability?.level > 0 && (
                  <PriorityIndicator text={metaDescription?.solvability?.text || ''} level={metaDescription?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/seo/what-is-a-meta-description/" target="_blank" rel="noopener noreferrer">
                    What Is a Meta Description?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/seo/how-to-write-meta-descriptions/" target="_blank" rel="noopener noreferrer">
                    How to Write Meta Descriptions
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Headings"
        tooltip="Headings consist of H1, H2, H3, H4, H5, and H6 headings. They appear on your page and divide content into organized sections."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={headings.iconType}
                showTooltip={false} 
              />
            }
          >
            {!headings?.isEmpty && headings?.data ? (
              <>
                <Table
                  data={[
                    ['H1', 'H2', 'H3', 'H4', 'H5'],
                    headingsCountRow
                  ]}
                  className="table-headings-count"
                />
                
                <div className="table-content-wrapper">
                  <Table
                    data={headingsSummaryData}
                    className="table-headings-summary"
                    id="headings-summary-table"
                  />
                  <div className="copy-button-wrapper">
                    <CopyButton targetSelector="#headings-summary-table" />
                  </div>
                </div>
              </>
            ) : (
              <p className="text-primary">{headings?.message || 'No headings were found.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={headingsExpanded}
            onClick={() => setHeadingsExpanded(!headingsExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={headingsExpanded}
        nestedBackgroundColor="blue"
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Heading Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Write informative, easy-to-follow heading tags</li>
                <li>Use heading tags, like H1s, H2s, and H3s, to organize content</li>
                <li>Feature core or related keywords in heading tags</li>
              </ul>
            </IconContent>
          }
          sideContent={
            headings?.importance?.level > 0 || headings?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {headings?.importance?.level > 0 && (
                  <PriorityIndicator text={headings?.importance?.text || ''} level={headings?.importance?.level || 0} />
                )}
                {headings?.solvability?.level > 0 && (
                  <PriorityIndicator text={headings?.solvability?.text || ''} level={headings?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/marketing/what-are-header-tags/" target="_blank" rel="noopener noreferrer">
                    What are Header Tags? [+4 Tips for Writing Website Headings]
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/seo/learn/can-h1-improve-seo/" target="_blank" rel="noopener noreferrer">
                    Can a H1 Tag Improve My SEO? | How H1 Affects SEO
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Content Analysis"
        tooltip="Your content analysis reveals the words or phrases that your content appears to target for SEO."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={contentAnalysis.iconType}
                showTooltip={false} 
              />
            }
          >
            {!contentAnalysis?.isEmpty && contentAnalysis?.data ? (
              <div className="table-content-wrapper">
                <p className="text-primary">{contentAnalysis?.message}</p>
                <Table
                  data={contentAnalysisData}
                  className="table-content-analysis"
                  id="content-analysis-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#content-analysis-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">{contentAnalysis?.message || 'Content analysis data is unavailable.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={contentAnalysisExpanded}
            onClick={() => setContentAnalysisExpanded(!contentAnalysisExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={contentAnalysisExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Content Analysis Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Focus on relevant keywords for your target audience</li>
                <li>Ensure keyword frequency is natural and not forced</li>
                <li>Use semantic variations and related terms</li>
              </ul>
            </IconContent>
          }
          sideContent={
            contentAnalysis?.importance?.level > 0 || contentAnalysis?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {contentAnalysis?.importance?.level > 0 && (
                  <PriorityIndicator text={contentAnalysis?.importance?.text || ''} level={contentAnalysis?.importance?.level || 0} />
                )}
                {contentAnalysis?.solvability?.level > 0 && (
                  <PriorityIndicator text={contentAnalysis?.solvability?.text || ''} level={contentAnalysis?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/seo/keyword-research/" target="_blank" rel="noopener noreferrer">
                    What Is Keyword Research? A Beginner's Guide
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/seo/content-optimization/" target="_blank" rel="noopener noreferrer">
                    Content Optimization: How to Optimize Content for SEO
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Alt Attribute"
        tooltip="Alt attribute describes alt or descriptive text that users and search engines can use to understand visual elements on your site."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={altAttribute.iconType}
                showTooltip={false} 
              />
            }
          >
            {altAttribute?.data ? (
              <>
                <p className="text-primary">{altAttribute?.message}</p>
                {altAttribute?.data?.missingAlt > 0 && altAttribute?.data?.missingAltImages?.length > 0 && (
              <div className="table-content-wrapper">
                    <p className="text-secondary">Images missing alt attributes:</p>
                    <Table
                      data={missingAltTableData}
                      className="table-missing-alt"
                      id="missing-alt-table"
                    />
                <div className="copy-button-wrapper">
                      <CopyButton targetSelector="#missing-alt-table" />
                </div>
              </div>
            )}
              </>
            ) : (
              <p className="text-primary">{altAttribute?.message || 'Image data is unavailable.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={altAttributeExpanded}
            onClick={() => setAltAttributeExpanded(!altAttributeExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={altAttributeExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Alt Attribute Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Add image alt text for all images</li>
                <li>Describe an image exactly as it appears, like "dog at beach"</li>
                <li>Include core or related keywords, like for product images</li>
              </ul>
            </IconContent>
          }
          sideContent={
            altAttribute?.importance?.level > 0 || altAttribute?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {altAttribute?.importance?.level > 0 && (
                  <PriorityIndicator text={altAttribute?.importance?.text || ''} level={altAttribute?.importance?.level || 0} />
                )}
                {altAttribute?.solvability?.level > 0 && (
                  <PriorityIndicator text={altAttribute?.solvability?.text || ''} level={altAttribute?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="https://www.webfx.com/blog/seo/seo-for-images/" target="_blank" rel="noopener noreferrer">
                    How to Do SEO for Images | SEO Tips for Images
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/seo/learn/visual-search/" target="_blank" rel="noopener noreferrer">
                    7 Tips for Optimizing Images for Visual Search
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="https://www.webfx.com/blog/seo/seo-for-images/" target="_blank" rel="noopener noreferrer">
                    8 SEO Tips for Image-Heavy Sites
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="In-page Links"
        tooltip="In-page links look at the number and percentage of external and internal links on your site."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={inPageLinks?.iconType}
                showTooltip={false} 
              />
            }
          >
            {inPageLinks?.data ? (
              <div className="table-content-wrapper">
                <p className="text-primary">
                  We found a total of <strong>{inPageLinks?.data?.totalLinks || 0}</strong> link{(inPageLinks?.data?.totalLinks || 0) === 1 ? '' : 's'} including <strong>{inPageLinks?.data?.linksToFiles || 0}</strong> link{(inPageLinks?.data?.linksToFiles || 0) === 1 ? '' : 's'} to files.
                </p>
                {(inPageLinks?.data?.internalFollow?.percentage > 0 || 
                  inPageLinks?.data?.internalNoFollow?.percentage > 0 ||
                  inPageLinks?.data?.externalFollow?.percentage > 0 ||
                  inPageLinks?.data?.externalNoFollow?.percentage > 0) && (
                <div className="progress-section">
                    {inPageLinks?.data?.internalFollow?.percentage > 0 && (
                  <div className="progress-row">
                    <div className="progress-labels">
                      <span className="progress-label">Internal Links:</span>
                          <span className="progress-value">Follow ({inPageLinks?.data?.internalFollow?.percentage || 0}%)</span>
                    </div>
                        <ProgressBar percentage={inPageLinks?.data?.internalFollow?.percentage || 0} color="#4DC1B9" />
                  </div>
                    )}
                    {inPageLinks?.data?.internalNoFollow?.percentage > 0 && (
                      <div className="progress-row">
                        <div className="progress-labels">
                          <span className="progress-label">Internal Links:</span>
                          <span className="progress-value">No Follow ({inPageLinks?.data?.internalNoFollow?.percentage || 0}%)</span>
                        </div>
                        <ProgressBar percentage={inPageLinks?.data?.internalNoFollow?.percentage || 0} color="#000000" />
                      </div>
                    )}
                    {inPageLinks?.data?.externalFollow?.percentage > 0 && (
                  <div className="progress-row">
                    <div className="progress-labels">
                      <span className="progress-label">External Links:</span>
                          <span className="progress-value">Follow ({inPageLinks?.data?.externalFollow?.percentage || 0}%)</span>
                    </div>
                        <ProgressBar percentage={inPageLinks?.data?.externalFollow?.percentage || 0} color="#EE4F54" />
                  </div>
                    )}
                    {inPageLinks?.data?.externalNoFollow?.percentage > 0 && (
                      <div className="progress-row">
                        <div className="progress-labels">
                          <span className="progress-label">External Links:</span>
                          <span className="progress-value">No Follow ({inPageLinks?.data?.externalNoFollow?.percentage || 0}%)</span>
                </div>
                        <ProgressBar percentage={inPageLinks?.data?.externalNoFollow?.percentage || 0} color="#EBAA49" />
                      </div>
                    )}
                  </div>
                )}
                {inPageLinks?.data?.links?.length > 0 && (
                  <>
                <Table
                      data={inPageLinksTableData}
                  className="table-inpage-links"
                  id="inpage-links-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#inpage-links-table" />
                </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-primary">{inPageLinks?.message || 'In-page links data is unavailable.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={inPageLinksExpanded}
            onClick={() => setInPageLinksExpanded(!inPageLinksExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={inPageLinksExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's In-Page Links Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Use descriptive anchor text</li>
                <li>Link to relevant and related content</li>
                <li>Ensure every page has at least 2-3 internal backlinks</li>
              </ul>
            </IconContent>
          }
          sideContent={
            inPageLinks?.importance?.level > 0 || inPageLinks?.solvability?.level > 0 ? (
            <PriorityIndicatorGroup>
                {inPageLinks?.importance?.level > 0 && (
                  <PriorityIndicator text={inPageLinks?.importance?.text || ''} level={inPageLinks?.importance?.level || 0} />
                )}
                {inPageLinks?.solvability?.level > 0 && (
                  <PriorityIndicator text={inPageLinks?.solvability?.text || ''} level={inPageLinks?.solvability?.level || 0} />
                )}
            </PriorityIndicatorGroup>
            ) : null
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/seo/glossary/what-is-internal-linking/" target="_blank" rel="noopener noreferrer">
                    What Is Internal Linking (And Why Does It Matter?)
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/blog/web-design/anchor-text/" target="_blank" rel="noopener noreferrer">
                    Why Anchor Text Matters [Guide]
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/seo/learn/do-outbound-links-affect-seo/" target="_blank" rel="noopener noreferrer">
                    Do Outbound Links Affect SEO?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Phone Numbers"
        tooltip="Phone numbers associated with your business."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={phoneNumbers?.iconType}
                showTooltip={false} 
              />
            }
          >
            {!phoneNumbers?.isEmpty && phoneNumbers?.data ? (
              <div className="table-content-wrapper">
                <Table
                  data={phoneNumbersTableData}
                  className="table-phone-numbers"
                  id="phone-numbers-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#phone-numbers-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">{phoneNumbers?.message || 'No phone numbers were found.'}</p>
            )}
          </IconContent>
        }
      />

      <ResultSubItem
        title="Web Feeds"
        tooltip="Web feeds associated with your business."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={webFeeds?.iconType}
                showTooltip={false} 
              />
            }
          >
            {!webFeeds?.isEmpty && webFeeds?.data ? (
              <div className="table-content-wrapper">
                <Table
                  data={webFeedsTableData}
                  className="table-web-feeds"
                  id="web-feeds-table"
                />
                <div className="copy-button-wrapper">
                  <CopyButton targetSelector="#web-feeds-table" />
                </div>
              </div>
            ) : (
              <p className="text-primary">{webFeeds?.message || 'No web feeds were found.'}</p>
            )}
          </IconContent>
        }
      />

      <ResultSubItem
        title="Google Preview"
        tooltip="What your website looks like in Google results."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={googlePreview?.iconType}
                showTooltip={false} 
              />
            }
          >
            {!googlePreview?.isEmpty && googlePreview?.data ? (
              <GooglePreview
                desktop={googlePreview?.data?.desktop}
                mobile={googlePreview?.data?.mobile}
              />
            ) : (
              <p className="text-primary">{googlePreview?.message || 'The Google Preview was not able to be generated.'}</p>
            )}
          </IconContent>
        }
      />
    </ResultsItem>
  );
};
